import { axiosClient } from '@/shared/apis/config';
import { useAuthInitialized } from '@/shared/provider/AuthProvider';
import { useTokenStore } from '@/shared/store/token-store';
import { ApiResponse, CustomQueryOptions } from '@/shared/types';
import { useQuery } from '@tanstack/react-query';
import { EditorDataResponse } from '../scriptSnippetApi/types';
import {
  getEditorDataQueryKey,
  QueryKeyUseGetEditorDataApi,
} from './get-editor-data-query-key';

type ResponseType = ApiResponse<EditorDataResponse>;

export const getEditorDataApi = async (
  projectId: number | string,
  storyId?: number | string,
  storyClipInfoId?: number | string,
) => {
  try {
    const storyIdStr = storyId?.toString();
    const isMultipleStoryIds = storyIdStr?.includes(',');

    const response = await axiosClient.get<ResponseType>(
      `/v2.0/video-editor/clip/data`,
      {
        params: {
          projectId,
          storyId: isMultipleStoryIds ? undefined : storyId,
          storyIds: isMultipleStoryIds ? storyIdStr : undefined,
          storyClipInfoId,
        },
      },
    );

    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const useGetEditorDataApi = ({
  projectId,
  storyId,
  storyClipInfoId,
  queryOptions,
}: {
  projectId: number | string;
  storyId?: number | string;
  storyClipInfoId?: number | string;
  queryOptions?: CustomQueryOptions<ResponseType>;
}) => {
  const { queryKey, enabled, ...rest } = queryOptions ?? {};
  const { isAuthInitialized } = useAuthInitialized();
  const { accessToken } = useTokenStore();
  const requestedEnabled = enabled ?? true;

  return useQuery({
    queryKey: getEditorDataQueryKey({
      projectId,
      storyId,
      storyClipInfoId,
      queryKey,
    }),
    queryFn: () => getEditorDataApi(projectId, storyId, storyClipInfoId),
    enabled: isAuthInitialized && !!accessToken && requestedEnabled,
    ...rest,
  });
};

export { QueryKeyUseGetEditorDataApi };
